import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  Renderer2,
  Inject,
  OnDestroy,
} from '@angular/core';
import { componentStylesByTheme } from '../utils/theme.utils';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import { tooltipStylesSAG, tooltipStylesSHS } from './tooltip.styles';

@Directive({
  selector: '[sag-ui-tooltip]',
})
export class TooltipDirective implements OnDestroy {
  @Input() tooltip = '';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

  private tooltipElement: HTMLElement | null = null;
  private isVisible = false;

  constructor(
    @Inject(THEME_TOKEN) private readonly theme: Theme,
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  @HostListener('mouseenter') onMouseEnter() {
    this.showTooltip();
  }

  @HostListener('mousemove') onMouseMove() {
    this.positionTooltip();
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.hideTooltip();
  }

  ngOnDestroy() {
    this.removeTooltip();
  }

  private showTooltip() {
    if (!this.isVisible && this.tooltip) {
      this.tooltipElement = this.renderer.createElement('div');

      const tooltipStyles = componentStylesByTheme(
        this.theme,
        tooltipStylesSAG,
        tooltipStylesSHS
      );

      if (!this.tooltipElement) return;
      this.tooltipElement.setAttribute('class', tooltipStyles());

      this.renderer.addClass(this.tooltipElement, 'tooltip');

      this.renderer.appendChild(
        this.tooltipElement,
        this.renderer.createText(this.tooltip)
      );
      this.renderer.appendChild(document.body, this.tooltipElement);
      this.positionTooltip();
      this.isVisible = true;
    }
  }

  private hideTooltip() {
    this.removeTooltip();
  }

  private removeTooltip() {
    if (this.isVisible && this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
      this.isVisible = false;
    }
  }

  private positionTooltip() {
    if (!this.tooltipElement) return;
    this.tooltipElement.style.position = 'absolute';
    this.tooltipElement.innerText = this.tooltip;

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    let top, left;
    const offset = 10;

    switch (this.tooltipPosition) {
      case 'bottom':
        top = hostRect.bottom + window.scrollY + offset;
        left =
          hostRect.left +
          hostRect.width / 2 -
          tooltipRect.width / 2 +
          window.scrollX;
        break;
      case 'top':
        top = hostRect.top - tooltipRect.height + window.scrollY - offset;
        left =
          hostRect.left +
          hostRect.width / 2 -
          tooltipRect.width / 2 +
          window.scrollX;
        break;
      case 'left':
        top =
          hostRect.top +
          hostRect.height / 2 -
          tooltipRect.height / 2 +
          window.scrollY;
        left = hostRect.left - tooltipRect.width + window.scrollX - offset;
        break;
      case 'right':
        top =
          hostRect.top +
          hostRect.height / 2 -
          tooltipRect.height / 2 +
          window.scrollY;
        left = hostRect.right + window.scrollX + offset;
        break;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  }
}
